import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Avatar, AvatarFallback, AvatarImage } from "../components/ui/avatar";
import { Separator } from "../components/ui/separator";
import { toast } from "sonner";
import { 
  User, 
  Mail, 
  Lock, 
  Save,
  Shield,
  Building2,
  Forward,
  Phone,
  Sparkles,
  Loader2
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SettingsPage({ user }) {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    forward_to_email: "",
    rep_phone: "",
    current_password: "",
    new_password: "",
    confirm_password: ""
  });

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      const response = await fetch(`${API}/auth/profile`, { credentials: "include" });
      if (response.ok) {
        const data = await response.json();
        setProfile(data);
        setFormData(prev => ({
          ...prev,
          name: data.user?.name || "",
          email: data.user?.email || "",
          forward_to_email: data.user?.forward_to_email || ""
        }));
      }
    } catch (error) {
      console.error("Failed to fetch profile:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleUpdateProfile = async (e) => {
    e.preventDefault();
    
    // Validation
    if (formData.new_password && formData.new_password !== formData.confirm_password) {
      toast.error("New passwords do not match");
      return;
    }
    
    if (formData.new_password && formData.new_password.length < 8) {
      toast.error("Password must be at least 8 characters");
      return;
    }
    
    // Check if email or password is changing - requires current password
    const emailChanging = formData.email !== profile?.user?.email;
    const passwordChanging = formData.new_password?.length > 0;
    
    if ((emailChanging || passwordChanging) && !formData.current_password) {
      toast.error("Current password is required to change email or password");
      return;
    }

    setSaving(true);
    try {
      const updateData = {};
      
      if (formData.name !== profile?.user?.name) {
        updateData.name = formData.name;
      }
      
      // Forwarding email
      if (formData.forward_to_email !== (profile?.user?.forward_to_email || "")) {
        updateData.forward_to_email = formData.forward_to_email;
      }

      // Rep cell phone (used for Twilio click-to-call)
      if (formData.rep_phone !== (profile?.user?.rep_phone || "")) {
        updateData.rep_phone = formData.rep_phone;
      }
      
      if (emailChanging) {
        updateData.email = formData.email;
        updateData.current_password = formData.current_password;
      }
      
      if (passwordChanging) {
        updateData.new_password = formData.new_password;
        updateData.current_password = formData.current_password;
      }

      const response = await fetch(`${API}/auth/profile`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(updateData)
      });

      const data = await response.json();

      if (response.ok) {
        toast.success(data.message || "Profile updated successfully");
        setFormData(prev => ({
          ...prev,
          current_password: "",
          new_password: "",
          confirm_password: ""
        }));
        fetchProfile();
      } else {
        toast.error(data.detail || "Failed to update profile");
      }
    } catch (error) {
      toast.error("Failed to update profile");
    } finally {
      setSaving(false);
    }
  };

  const getInitials = (name) => {
    if (!name) return "U";
    return name.split(" ").map(n => n[0]).join("").toUpperCase().slice(0, 2);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto space-y-6 animate-fade-in-up" data-testid="settings-page">
      {/* Header */}
      <div>
        <h1 className="font-heading text-3xl font-bold tracking-tight text-slate-900">
          Account Settings
        </h1>
        <p className="text-slate-500 mt-1">
          Manage your profile and account preferences
        </p>
      </div>

      {/* Profile Card */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <User className="w-5 h-5" />
            Profile Information
          </CardTitle>
          <CardDescription>Update your personal information</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-4 mb-6">
            <Avatar className="h-16 w-16">
              <AvatarImage src={profile?.user?.picture} />
              <AvatarFallback className="text-lg">
                {getInitials(profile?.user?.name)}
              </AvatarFallback>
            </Avatar>
            <div>
              <p className="font-medium text-lg">{profile?.user?.name}</p>
              <p className="text-sm text-slate-500">{profile?.user?.email}</p>
              {profile?.user?.system_role === "platform_owner" && (
                <span className="inline-flex items-center gap-1 text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded mt-1">
                  <Shield className="w-3 h-3" />
                  Platform Owner
                </span>
              )}
            </div>
          </div>

          <form onSubmit={handleUpdateProfile} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name">Full Name</Label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  className="pl-10"
                  data-testid="input-name"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="email">Email Address</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  id="email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  className="pl-10"
                  data-testid="input-email"
                />
              </div>
              <p className="text-xs text-slate-500">
                Changing email requires your current password
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="forward_to_email">Email Forwarding</Label>
              <div className="relative">
                <Forward className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  id="forward_to_email"
                  name="forward_to_email"
                  type="email"
                  value={formData.forward_to_email}
                  onChange={handleChange}
                  className="pl-10"
                  placeholder="optional-forward@example.com"
                  data-testid="input-forward-email"
                />
              </div>
              <p className="text-xs text-slate-500">
                Appointment notifications will also be sent to this address if set
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="rep_phone">Your Cell Phone (for click-to-call)</Label>
              <div className="relative">
                <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  id="rep_phone"
                  name="rep_phone"
                  type="tel"
                  value={formData.rep_phone}
                  onChange={handleChange}
                  className="pl-10"
                  placeholder="+15551234567"
                  data-testid="input-rep-phone"
                />
              </div>
              <p className="text-xs text-slate-500">
                E.164 format required (e.g. +15551234567). When you place a call from a lead, Twilio rings this number first, then bridges you to the lead.
              </p>
            </div>

            <Separator className="my-6" />

            <div className="space-y-4">
              <h3 className="font-medium flex items-center gap-2">
                <Lock className="w-4 h-4" />
                Change Password
              </h3>
              
              <div className="space-y-2">
                <Label htmlFor="current_password">Current Password</Label>
                <Input
                  id="current_password"
                  name="current_password"
                  type="password"
                  value={formData.current_password}
                  onChange={handleChange}
                  placeholder="Enter current password"
                  data-testid="input-current-password"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="new_password">New Password</Label>
                  <Input
                    id="new_password"
                    name="new_password"
                    type="password"
                    value={formData.new_password}
                    onChange={handleChange}
                    placeholder="New password"
                    data-testid="input-new-password"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="confirm_password">Confirm Password</Label>
                  <Input
                    id="confirm_password"
                    name="confirm_password"
                    type="password"
                    value={formData.confirm_password}
                    onChange={handleChange}
                    placeholder="Confirm new password"
                    data-testid="input-confirm-password"
                  />
                </div>
              </div>
              <p className="text-xs text-slate-500">
                Leave blank to keep current password
              </p>
            </div>

            <div className="pt-4">
              <Button 
                type="submit" 
                disabled={saving}
                data-testid="save-profile-btn"
              >
                {saving ? (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                ) : (
                  <>
                    <Save className="w-4 h-4 mr-2" />
                    Save Changes
                  </>
                )}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>

      {/* Organization Info (Read-only) */}
      {profile?.organization && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Building2 className="w-5 h-5" />
              Organization
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-slate-500">Name</span>
                <span className="font-medium">{profile.organization.name}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-500">Plan</span>
                <span className="font-medium uppercase">{profile.organization.plan}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-500">Seats</span>
                <span className="font-medium">
                  {profile.organization.seats_used} / {profile.organization.seats_purchased}
                </span>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Industry Profile — drives Sales Intelligence context */}
      {profile?.organization && <OrgIndustryProfileCard />}
    </div>
  );
}

function OrgIndustryProfileCard() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    industry: "",
    services_offered: "",
    target_audience: "",
    value_proposition: "",
    company_description: "",
  });

  useEffect(() => {
    fetch(`${API}/organization`, { credentials: "include" })
      .then((r) => (r.ok ? r.json() : null))
      .then((org) => {
        if (org) {
          setForm({
            industry: org.industry || "",
            services_offered: org.services_offered || "",
            target_audience: org.target_audience || "",
            value_proposition: org.value_proposition || "",
            company_description: org.company_description || "",
          });
        }
      })
      .finally(() => setLoading(false));
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API}/organization`, {
        method: "PUT",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Save failed");
      toast.success("Industry profile saved — Sales Intelligence will use it on the next generate");
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) return null;

  return (
    <Card data-testid="org-industry-profile-card">
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2">
          <Sparkles className="w-5 h-5 text-blue-600" />
          Industry Profile
        </CardTitle>
        <CardDescription>
          Tells Sales Intelligence what YOU sell so every report, pitch, and email is tailored to your business — not a generic CRM script.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <div>
          <Label htmlFor="org-industry">Your industry</Label>
          <Input
            id="org-industry"
            placeholder="e.g. Real Estate, Marketing Agency, Healthcare, SaaS, Retail"
            value={form.industry}
            onChange={(e) => setForm({ ...form, industry: e.target.value })}
            data-testid="org-industry-input"
          />
        </div>
        <div>
          <Label htmlFor="org-services">Services / products offered</Label>
          <Textarea
            id="org-services"
            rows={2}
            placeholder="e.g. Residential listings + buyer representation in San Diego County"
            value={form.services_offered}
            onChange={(e) => setForm({ ...form, services_offered: e.target.value })}
            data-testid="org-services-input"
          />
        </div>
        <div>
          <Label htmlFor="org-audience">Target audience / customer type</Label>
          <Textarea
            id="org-audience"
            rows={2}
            placeholder="e.g. First-time homebuyers earning $80k+ in coastal cities"
            value={form.target_audience}
            onChange={(e) => setForm({ ...form, target_audience: e.target.value })}
            data-testid="org-audience-input"
          />
        </div>
        <div>
          <Label htmlFor="org-value-prop">Value proposition</Label>
          <Textarea
            id="org-value-prop"
            rows={2}
            placeholder="e.g. We close 30% faster than the average San Diego agent and negotiate average $14k off list price"
            value={form.value_proposition}
            onChange={(e) => setForm({ ...form, value_proposition: e.target.value })}
            data-testid="org-value-prop-input"
          />
        </div>
        <div>
          <Label htmlFor="org-description">Company description (optional)</Label>
          <Textarea
            id="org-description"
            rows={2}
            placeholder="A short paragraph — anything else the AI should know about your business"
            value={form.company_description}
            onChange={(e) => setForm({ ...form, company_description: e.target.value })}
            data-testid="org-description-input"
          />
        </div>
        <div className="flex justify-end">
          <Button onClick={save} disabled={saving} data-testid="save-org-industry-btn">
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
            Save industry profile
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
